import { toSnackCase } from "./utils";
import {
  normalizePermission,
  resolvePermissionMetadata,
  normalizePermissionList,
} from "./PermissionMetadata";
import React, { useContext, useMemo, useEffect } from "react";
import { AuthContext } from "../Context/AuthContext";
import { PermissionDenied } from "../Pages/PageNotFound/PageNotFound";
import { EqupRoundLoaderWithoutOverlay } from "../Components/Common/Loader/RoundLoader";

export const fetchUserRole = (userData) => {
  if (userData && userData !== null && userData !== undefined) {
    const { authenticated, authData } = userData;
    if (authenticated && authData.currentTeam) {
      const { id, name, permissions } = authData.currentTeam.pivot.role;
      const currentPermissions =
        permissions.length > 0 &&
        permissions.map(
          (item) =>
            item && {
              id: item.id,
              industry: JSON.parse(item.industry),
              label: item.label,
              name: item.name,
              access_type: item.pivot.access_type,
            }
        );
      return {
        error: null,
        role: { id: id, name: name },
        permissions: currentPermissions,
      };
    } else {
      return {
        error: "User is not authorised.",
        role: null,
        permissions: null,
      };
    }
  }
};

const getNormalizedPermissions = (userData) =>
  normalizePermissionList(
    userData?.authData?.currentUserPermission || []
  ).filter((perm) => perm && perm.name);

export const ENGAGEMENT_CHANNEL_PERMISSION_MAP = {
  1: { key: "email", permission: "engagement_email:view" },
  2: { key: "sms", permission: "engagement_sms:view" },
  3: { key: "chat", permission: "engagement_chat:view" },
  4: { key: "whatsapp", permission: "engagement_whatsapp:view" },
  5: { key: "appointment", permission: "engagement_appointment:view" },
  6: { key: "booking", permission: "engagement_booking:view" },
};

export const getPermission = (userData, name) => {
  const permissions = getNormalizedPermissions(userData);
  return permissions.find((perm) => perm.name === name) || null;
};

export const hasPermission = (userData, name, accessTypes = null) => {
  const permission = getPermission(userData, name);
  if (!permission) return false;
  if (!accessTypes) return true;
  const allowedTypes = Array.isArray(accessTypes)
    ? accessTypes
    : [accessTypes];
  return allowedTypes.includes(permission.access_type || permission.pivot?.access_type);
};

export const getEngagementChannelPermissions = (userData) => {
  const permissions = getNormalizedPermissions(userData);
  const byName = new Map(permissions.map((permission) => [permission.name, permission]));
  const legacyPermission = byName.get("engagement:view") || null;
  const hasSpecificPermissions = Object.values(ENGAGEMENT_CHANNEL_PERMISSION_MAP).some(
    ({ permission }) => byName.has(permission)
  );

  return Object.entries(ENGAGEMENT_CHANNEL_PERMISSION_MAP).reduce(
    (acc, [channelValue, config]) => {
      const permission =
        byName.get(config.permission) || (!hasSpecificPermissions ? legacyPermission : null);
      const accessType =
        permission?.access_type || permission?.pivot?.access_type || null;

      acc[channelValue] = {
        ...config,
        isAllow: Boolean(permission),
        accessType,
      };

      return acc;
    },
    {}
  );
};

export const getModulePermissions = (
  userData,
  module,
  { exactMatch = false } = {}
) => {
  const permissions = getNormalizedPermissions(userData);
  if (!module) return permissions;

  return permissions.filter((permission) => {
    if (!permission.name) return false;
    if (exactMatch) {
      const [moduleName] = permission.name.split(":");
      return moduleName === module;
    }
    return permission.name.includes(module);
  });
};

export const getPagePermissions = (userData, module, exactMatch = false) => {
  const permissions = {};
  const modulePermissions = getModulePermissions(userData, module, {
    exactMatch,
  });

  modulePermissions.forEach((permission) => {
    const normalized = normalizePermission(permission);
    if (!normalized || !normalized.name) return;
    const meta = resolvePermissionMetadata(normalized.name);
    const label = normalized.label || meta.label;
    const key = toSnackCase(label || normalized.name);
    const moduleKey =
      key && module ? toSnackCase(`${label}_${module}`) : null;
    const payload = { isAllow: true, value: normalized };
    if (key) permissions[key] = payload;
    if (moduleKey) permissions[moduleKey] = payload;
    if (key === "access" || normalized.name.endsWith(":access")) {
      permissions.view = payload;
      if (module) {
        permissions[toSnackCase(`view_${module}`)] = payload;
      }
    }
  });

  return Object.keys(permissions).length > 0 ? permissions : {};
};

/**
 * Hook to standardize permission gating for pages.
 * Usage:
 *   const { loading, denied, pagePermissions } = usePagePermission("contact");
 *   if (loading) return <EqupRoundLoaderWithoutOverlay />;
 *   if (denied) return <PermissionDenied />;
 *   // render page with pagePermissions
 */
export const usePagePermission = (module, { minParams = 0 } = {}) => {
  const { userData } = useContext(AuthContext);
  const isAuthLoading = userData?.isFetching;
  const hasUser = Boolean(userData?.authData);
  const currentPermissions = userData?.authData?.currentUserPermission;
  const permsReady = currentPermissions !== undefined;
  const [resolved, setResolved] = React.useState(false);
  const [emptyResolved, setEmptyResolved] = React.useState(false);

  const pagePermissions = useMemo(() => {
    if (!hasUser || !permsReady) return null;
    return getPagePermissions(userData, module);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUser, permsReady, userData, module]);

  useEffect(() => {
    if (pagePermissions !== null && pagePermissions !== undefined) {
      setResolved(true);
    }
  }, [pagePermissions]);

  useEffect(() => {
    if (pagePermissions && Object.keys(pagePermissions).length === 0) {
      setEmptyResolved(false);
      const t = setTimeout(() => setEmptyResolved(true), 300);
      return () => clearTimeout(t);
    } else {
      setEmptyResolved(false);
    }
  }, [pagePermissions]);

  const loading =
    Boolean(isAuthLoading) ||
    (hasUser && (!permsReady || !resolved)) ||
    (pagePermissions && Object.keys(pagePermissions).length === 0 && !emptyResolved);
  const denied =
    !loading &&
    hasUser &&
    pagePermissions &&
    (!pagePermissions.view || !pagePermissions.view.isAllow);

  // Helper to render gating quickly
  const renderGuard = () => {
    if (loading) return <EqupRoundLoaderWithoutOverlay />;
    if (denied) return <PermissionDenied />;
    return null;
  };

  const shouldFetch = !loading && !denied && minParams === 0;

  return { loading, denied, pagePermissions, renderGuard, shouldFetch };
};

// export const getPagePermissions = (userData, module) => {
//   const userRole = fetchUserRole(userData);
//   let permissions = {};
//   if (
//     userRole.error === null &&
//     userRole.permissions &&
//     userRole.permissions.length > 0
//   ) {
//     userRole.permissions.forEach((item) => {
//       if (item.name.indexOf(module) > -1) {
//         permissions = {
//           ...permissions,
//           [toSnackCase(item.label)]: { isAllow: true, value: item },
//         };
//       }
//     });
//     //console.log(permissions);
//     return permissions;
//   } else {
//     return null;
//   }
// };
/***************************/
